import {
  GAME_SIZE_IDS,
  legalActions,
  otherSide,
  type Action,
  type GameSizeId,
  type GameState,
  type Ruleset,
  type Side,
} from '@brig/rules-engine';
import type { CreateGameBody, RuleOptions } from '@brig/api-types';
import { useCallback, useEffect, useMemo } from 'react';
import { hintsFor } from '../hints.ts';
import {
  buildHistory,
  current,
  newSetup,
  randomSeed,
  type Colours,
  type History,
  type Nations,
} from '../history.ts';
import { liveSquadron } from '../turnMachine.ts';
import { useTurn, type CommitLogic } from '../useTurn.ts';
import { logFileName, parseLog, toLog } from './logFile.ts';

export { hintsFor, type Hints } from '../hints.ts';

/** Where the screen's game comes from and goes to. */
export interface GameSource {
  /** The game as it stands when the screen opens; read once. */
  readonly initial: () => History;
  /** Keeps the game: the server. Absent, the browser's own rules engine does (tests). */
  readonly commit?: CommitLogic;
  /** Rewind, fixed seeds, rule options and logs are allowed. */
  readonly devTools: boolean;
  /** The side played elsewhere, by the computer or online; absent when people play both here. */
  readonly remote?: Side;
  /**
   * Online: calls back with the game as it stands whenever the other player changes it, until
   * the returned function is called.
   */
  readonly subscribe?: (onChange: (history: History) => void) => () => void;
  /** Starts another game and switches the screen to it. Absent, the screen restarts itself. */
  readonly newGame?: (body: CreateGameBody) => Promise<void>;
  /** Loads a log file as another game and switches to it. */
  readonly importLog?: (log: unknown) => Promise<void>;
  /** The game's log file; built from the history when absent. */
  readonly exportLog?: () => Promise<unknown>;
  /** The sizes a new game may take; every size when absent. */
  readonly sizes?: readonly GameSizeId[];
  /** Where "Exit" leads once the game is over; the logbook when absent. */
  readonly exitTo?: string;
}

/** A game the browser keeps by itself, from a random seed. */
export const localSource = (
  history: () => History = () => buildHistory(newSetup(randomSeed())),
): GameSource => ({
  initial: history,
  devTools: true,
});

const message = (e: unknown) => (e instanceof Error ? e.message : String(e));

/** The game screen's turn machine (game/) plus the menu's tools around it. */
export function useGameScreen(source: GameSource) {
  const { snapshot, send } = useTurn(source.initial, source.commit, source.remote ?? null);
  useEffect(
    () =>
      source.subscribe?.((history) => {
        send({ type: 'REMOTE', history });
      }),
    [send, source],
  );
  const history = snapshot.context.history;
  const state: GameState = current(history);

  const side = state.turn.side;
  const legal = useMemo(() => legalActions(state, side), [state, side]);
  const raw = snapshot.context.selected;
  // The selection may go stale when the position changes (undo, sinking…): filter on read.
  const selected = raw && !state.ships.some((s) => s.id === raw && s.sunk) ? raw : null;
  const squadron = useMemo(() => new Set(liveSquadron(snapshot.context)), [snapshot.context]);
  const hints = useMemo(() => hintsFor(state, legal, selected), [state, legal, selected]);
  const fire = snapshot.context.fire;
  // The panel's target and the shooters checked in it, for the board's rings.
  const fireShips = useMemo(
    () =>
      new Set(
        Object.entries(fire?.shots ?? {})
          .filter(([, n]) => n > 0)
          .map(([id]) => id),
      ),
    [fire],
  );

  const apply = useCallback(
    (action: Action) => {
      send({ type: 'APPLY', action });
    },
    [send],
  );

  const onUnit = useCallback(
    (id: string) => {
      send({ type: 'TAP_UNIT', id });
    },
    [send],
  );

  const onCell = useCallback(
    (cell: string) => {
      send({ type: 'TAP_CELL', cell });
    },
    [send],
  );

  /** The seed and rule options are dev tools: the server picks the seed otherwise. */
  const newGame = useCallback(
    (seed: number | null, ruleset: RuleOptions, nations: Nations, colours: Colours, size: GameSizeId) => {
      if (source.newGame) {
        const dev = source.devTools ? { ...(seed === null ? {} : { seed }), rules: ruleset } : {};
        source.newGame({ size, nations, colours, ...dev }).catch((e: unknown) => {
          send({ type: 'FAIL', message: message(e) });
        });
        return;
      }
      try {
        const setup = newSetup(seed ?? randomSeed(), ruleset as Partial<Ruleset>, nations, size);
        send({ type: 'RESET', history: buildHistory(setup, [], nations, colours) });
      } catch (e) {
        send({ type: 'FAIL', message: message(e) });
      }
    },
    [send, source],
  );

  const load = useCallback(
    (text: string) => {
      if (source.importLog) {
        let json: unknown;
        try {
          json = JSON.parse(text);
        } catch {
          send({ type: 'FAIL', message: 'The file is not valid JSON.' });
          return;
        }
        source.importLog(json).catch((e: unknown) => {
          send({ type: 'FAIL', message: message(e) });
        });
        return;
      }
      try {
        const log = parseLog(text);
        send({ type: 'RESET', history: buildHistory(log.setup, log.actions, log.nations, log.colours) });
      } catch (e) {
        send({ type: 'FAIL', message: message(e) });
      }
    },
    [send, source],
  );

  /** The log as a file to keep or to load again later (§11). */
  const exportLog = useCallback(() => {
    const download = (log: unknown) => {
      const blob = new Blob([JSON.stringify(log, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = logFileName(history);
      a.click();
      URL.revokeObjectURL(url);
    };
    if (!source.exportLog) {
      download(toLog(history));
      return;
    }
    source.exportLog().then(download, (e: unknown) => {
      send({ type: 'FAIL', message: message(e) });
    });
  }, [history, send, source]);

  return {
    snapshot,
    send,
    history,
    state,
    legal,
    hints,
    selected,
    setSelected: (id: string | null) => {
      send({ type: 'SELECT', id });
    },
    squadron,
    fire,
    fireShips,
    onUnit,
    onCell,
    apply,
    undo: () => {
      send({ type: 'UNDO' });
    },
    rewind: () => {
      send({ type: 'REWIND' });
    },
    newGame,
    load,
    exportLog,
    devTools: source.devTools,
    sizes: source.sizes ?? GAME_SIZE_IDS,
    exitTo: source.exitTo ?? '/logbook',
    recolour: (colours: Colours) => {
      send({ type: 'RECOLOUR', colours });
    },
    opponent: otherSide(side),
  };
}
